import { useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import logger from '../utils/logger';
import offlineDB from '../utils/offlineDB';

// Jun-2026 (auditoría D-07): F5 / relanzar la PWA en el detalle con mala cobertura
// colgaba sin límite y acababa en "Error al cargar el evento". Ahora: 8 s y caché.
const READ_TIMEOUT_MS = 8000;
const fetchPlaylistWithCacheFallback = async (API, playlistId) => {
  try {
    const token = localStorage.getItem('token');
    const response = await axios.get(`${API}/playlists/${playlistId}`, {
      headers: { Authorization: `Bearer ${token}` },
      timeout: READ_TIMEOUT_MS,
    });
    return response.data;
  } catch (error) {
    const cached = await offlineDB.getPlaylistById(playlistId).catch(() => null);
    if (cached) {
      logger.log('[MainApp] Evento cargado desde caché offline:', playlistId);
      toast.info('📴 Evento cargado desde caché offline', { duration: 3000 });
      return cached;
    }
    throw error;
  }
};

/**
 * useUrlEventSync
 *
 * Sincroniza los query-params de la URL con el estado de navegación de la app:
 *
 *   ?view=playlists                           → setView('playlists')
 *   ?view=library                             → setView('library')
 *   ?view=playlist-detail&playlistId=<id>     → carga la playlist y entra al detalle
 *   ?openEvent=<id>&liveMode=1                → abre el evento en modo En Vivo (badge "Hoy")
 *
 * Los flags `?openTaskForm=true` y `?openDossier=true` los gestiona
 * `useUrlActionTriggers` (extraído aparte).
 *
 * Extraído de `App.js` (feb-2026) — antes vivía como `useEffect` gigante dentro
 * de `AuthenticatedApp`.
 */
export function useUrlEventSync({
  API,
  isAuthenticated,
  location,
  navigate,
  currentPlaylist,
  setCurrentPlaylist,
  setGeneratedCustomList,
  setFilteredCustomList,
  setView,
  setIsLiveMode,
  setLoadedTabs,
  setCurrentTab,
}) {
  useEffect(() => {
    if (!isAuthenticated) {
      logger.log('[MainApp URL Effect] Skipping - not authenticated');
      return;
    }

    const params = new URLSearchParams(location.search);
    const viewParam = params.get('view');
    const playlistIdParam = params.get('playlistId');
    const openEventParam = params.get('openEvent');
    const liveModeParam = params.get('liveMode');

    // Solo procesar si hay parámetros relevantes
    if (
      !viewParam &&
      !params.get('openTaskForm') &&
      !params.get('openDossier') &&
      !openEventParam
    ) {
      // URL sin params. Si veníamos de un playlist-detail (currentPlaylist
      // sigue seteado) es porque el usuario navegó fuera pero el state no se
      // limpió: hacemos el sync. Esto arregla el bug "Volver a Playlists
      // vuelve al mismo evento": setState + navigate en el mismo handler
      // hacían que el efecto viera location.search stale y re-entrara al
      // detalle. Aquí, en cambio, es el propio hook quien limpia el state al
      // detectar la URL final vacía.
      if (currentPlaylist) {
        logger.log('[MainApp] URL sin params + currentPlaylist activo → limpiando state');
        setCurrentPlaylist(null);
        setView('playlists');
      }
      return;
    }

    logger.log('[MainApp URL Effect] Processing URL params:', {
      viewParam,
      playlistIdParam,
      openEventParam,
      path: location.pathname,
      search: location.search,
    });

    // openTaskForm / openDossier los gestiona useUrlActionTriggers
    if (params.get('openTaskForm') === 'true') return;
    if (params.get('openDossier') === 'true') return;

    // Badge "Hoy" en cabecera: ?openEvent={id}&liveMode=1 → cargar evento
    // + activar Modo En Vivo en 1 tap desde cualquier ruta.
    if (openEventParam) {
      const shouldEnableLive = liveModeParam === '1' || liveModeParam === 'true';
      const loadAndOpen = async () => {
        try {
          const response = { data: await fetchPlaylistWithCacheFallback(API, openEventParam) };
          setCurrentPlaylist(response.data);
          if (response.data?.lista_personalizada_generada?.length > 0) {
            try {
              const songsResponse = await axios.post(`${API}/songs/by-ids`, {
                ids: response.data.lista_personalizada_generada,
              }, { timeout: READ_TIMEOUT_MS });
              setGeneratedCustomList(songsResponse.data.songs);
              setFilteredCustomList(songsResponse.data.songs);
            } catch (err) {
              console.error('[MainApp] Error loading custom list:', err);
            }
          }
          setView('playlist-detail');
          if (shouldEnableLive) {
            setIsLiveMode(true);
            toast.success('🔴 Modo En Vivo Activado');
          }
        } catch (error) {
          console.error('[MainApp] Error opening event from URL:', error);
          toast.error('No se pudo abrir el evento');
        } finally {
          // Consumir `openEvent`+`liveMode` (triggers de un solo uso) pero dejar el
          // deep-link estable `?view=playlist-detail&playlistId=<id>` → F5 y share OK.
          navigate(`/?view=playlist-detail&playlistId=${openEventParam}`, { replace: true });
        }
      };
      loadAndOpen();
      return;
    }

    if (viewParam === 'playlists') {
      logger.log('[MainApp] Setting view to playlists');
      setView('playlists');
      navigate('/', { replace: true });
      return;
    }

    if (viewParam === 'library') {
      setView('library');
      return;
    }

    if (viewParam === 'playlist-detail' && playlistIdParam) {
      // GUARDA DE IDEMPOTENCIA: si la playlist ya está cargada, NO volver a
      // fetchear ni resetear las tabs (esto es lo que causaba que al pulsar
      // cualquier pestaña dentro del evento, se reseteara a "lista"). El efecto
      // se re-ejecuta cuando cambia `currentPlaylist` (p.ej. al actualizar
      // peticiones, documentos, etc.), y sin este guard, cada re-render
      // reseteaba `currentTab` a 'lista'.
      if (currentPlaylist?.id === playlistIdParam) {
        logger.log('[MainApp] Playlist already loaded, skipping reload:', playlistIdParam);
        setView('playlist-detail');
        return;
      }
      // Cargar la playlist si viene el ID en la URL
      logger.log('[MainApp] Loading playlist from URL:', playlistIdParam);
      const loadPlaylist = async () => {
        try {
          const response = { data: await fetchPlaylistWithCacheFallback(API, playlistIdParam) };
          logger.log(
            '[MainApp] Playlist loaded successfully:',
            response.data?.evento || response.data?.nombre_novios
          );
          setCurrentPlaylist(response.data);
          setLoadedTabs(new Set(['lista']));
          setCurrentTab('lista');
          setView('playlist-detail');
          // NO limpiar URL: dejamos `?view=playlist-detail&playlistId=<id>` estable
          // para que F5, compartir por WhatsApp y botón atrás funcionen correctamente.
        } catch (error) {
          console.error('[MainApp] Error loading playlist from URL:', error);
          toast.error('Error al cargar el evento');
          setView('playlists');
          // En error sí limpiamos para no reintentar en un loop de F5.
          navigate('/', { replace: true });
        }
      };
      loadPlaylist();
      return;
    }

    if (viewParam === 'playlist-detail' && currentPlaylist) {
      logger.log('[MainApp] Using existing currentPlaylist for playlist-detail');
      setView('playlist-detail');
      // Sincronizar la URL con el ID actual para permitir F5/share aunque el
      // consumidor original (p.ej. legacy nav) no lo hubiese incluido.
      if (currentPlaylist?.id) {
        navigate(`/?view=playlist-detail&playlistId=${currentPlaylist.id}`, { replace: true });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- setters estables; deps controladas manualmente
  }, [location.search, navigate, currentPlaylist, isAuthenticated]);
}
